import React, { useState, useEffect, useCallback, useRef } from "react";
import { CircularProgress, Typography, Box, Divider } from "@material-ui/core";
import moment from "moment";
import ReleaseNotesForm from "./ReleaseNotesForm";
import apiClient from "../../../auth/apiClient";
import ReleaseNotesSettingsService from "../../../services/ReleaseNotesSettingsService";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useCurrentFacilityTimezone from "../../../hooks/useCurrentFacilityTimezone";
import { useStyles } from "./ReleaseNotesForm.styles";

const releaseNotesSettingsService = new ReleaseNotesSettingsService(apiClient);

const defaultSettings = {
  releaseNotesEnabled: false,
  releaseNotesUrl: "",
  displayFrom: null,
  displayTo: null,
  feedbackEnabled: false,
  feedbackButtonLabel: "Feedback",
  feedbackLink: "",
};

const ReleaseNotesPage = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dateError, setDateError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const settingsRef = useRef(settings);
  const lastSavedRef = useRef(settings);
  const classes = useStyles();

  const enqueueSnackbar = useEnqueueSnackbar();
  const { convertUtcDate, convertToFacilityTimezoneUtc } =
    useCurrentFacilityTimezone();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await releaseNotesSettingsService.get();
        const data = response.data;
        const loaded = {
          ...data,
          displayFrom: data.displayFrom
            ? convertUtcDate(data.displayFrom)
            : null,
          displayTo: data.displayTo ? convertUtcDate(data.displayTo) : null,
        };
        setSettings(loaded);
        settingsRef.current = loaded;
        lastSavedRef.current = loaded;
        setError(false);
      } catch (err) {
        enqueueSnackbar("Unable to load settings", {
          variant: "error",
          tag: "releaseNotesSettingsLoadError",
        });
        setSettings(defaultSettings);
        settingsRef.current = defaultSettings;
        lastSavedRef.current = defaultSettings;
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const validateDateRange = useCallback((from, to) => {
    if (from && to) {
      // Compare using the displayed date/time values only (format level),
      // ignoring timezone offsets since from may be facility-tz moment
      // and to may be a local browser moment from the picker
      const fromStr = moment(from).format("YYYY-MM-DD HH:mm");
      const toStr = moment(to).format("YYYY-MM-DD HH:mm");
      if (fromStr > toStr) {
        return "Display From must be earlier than or equal to Display To";
      }
    }
    return "";
  }, []);

  const isValidUrl = useCallback((url) => {
    if (!url) return true;
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;
      // Must have :// after protocol and a domain with at least one dot
      if (!url.match(/^https?:\/\/.+\..+/)) return false;
      return true;
    } catch {
      return false;
    }
  }, []);

  const validateFields = useCallback((currentSettings) => {
    const errors = {};
    if (currentSettings.releaseNotesEnabled && !(currentSettings.releaseNotesUrl || "").trim()) {
      errors.releaseNotesUrl = "URL is required when Release Notes is enabled";
    } else if ((currentSettings.releaseNotesUrl || "").trim() && !isValidUrl((currentSettings.releaseNotesUrl || "").trim())) {
      errors.releaseNotesUrl = "Please enter a valid URL (e.g., https://example.com)";
    }
    if (!(currentSettings.feedbackButtonLabel || "").trim()) {
      errors.feedbackButtonLabel = "Button label is required";
    }
    if (currentSettings.feedbackEnabled && !(currentSettings.feedbackLink || "").trim()) {
      errors.feedbackLink = "Feedback link is required when Feedback is enabled";
    } else if ((currentSettings.feedbackLink || "").trim() && !isValidUrl((currentSettings.feedbackLink || "").trim())) {
      errors.feedbackLink = "Please enter a valid URL (e.g., https://example.com)";
    }
    return errors;
  }, [isValidUrl]);

  const hasChanged = useCallback((currentSettings) => {
    const last = lastSavedRef.current;
    return (
      currentSettings.releaseNotesEnabled !== last.releaseNotesEnabled ||
      currentSettings.releaseNotesUrl !== last.releaseNotesUrl ||
      currentSettings.feedbackEnabled !== last.feedbackEnabled ||
      currentSettings.feedbackButtonLabel !== last.feedbackButtonLabel ||
      currentSettings.feedbackLink !== last.feedbackLink ||
      String(currentSettings.displayFrom || "") !== String(last.displayFrom || "") ||
      String(currentSettings.displayTo || "") !== String(last.displayTo || "")
    );
  }, []);

  const saveSettings = useCallback(
    async (currentSettings) => {
      if (!hasChanged(currentSettings)) {
        return;
      }

      const from = currentSettings.displayFrom;
      const to = currentSettings.displayTo;
      if (from && to) {
        const fromStr = moment(from).format("YYYY-MM-DD HH:mm");
        const toStr = moment(to).format("YYYY-MM-DD HH:mm");
        if (fromStr > toStr) {
          return;
        }
      }

      const errors = validateFields(currentSettings);
      setFieldErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }

      try {
        const payload = {
          ...currentSettings,
          releaseNotesUrl: (currentSettings.releaseNotesUrl || "").trim(),
          feedbackButtonLabel: (currentSettings.feedbackButtonLabel || "").trim(),
          feedbackLink: (currentSettings.feedbackLink || "").trim(),
          displayFrom: currentSettings.displayFrom
            ? convertToFacilityTimezoneUtc(currentSettings.displayFrom)
            : null,
          displayTo: currentSettings.displayTo
            ? convertToFacilityTimezoneUtc(currentSettings.displayTo)
            : null,
        };

        const response = await releaseNotesSettingsService.put(payload);
        const data = response.data;

        const saved = {
          ...data,
          displayFrom: data.displayFrom
            ? convertUtcDate(data.displayFrom)
            : null,
          displayTo: data.displayTo ? convertUtcDate(data.displayTo) : null,
        };
        setSettings(saved);
        settingsRef.current = saved;
        lastSavedRef.current = saved;
      } catch (err) {
        const apiMessage =
          err?.response?.data?.message ||
          err?.response?.data?.validationErrors?.[0] ||
          "Failed to save settings";
        enqueueSnackbar(apiMessage, {
          variant: "error",
          tag: "releaseNotesSettingsError",
        });
      }
    },
    [convertToFacilityTimezoneUtc, convertUtcDate, enqueueSnackbar, hasChanged, validateFields]
  );

  const handleChange = useCallback(
    (field, value, autoSave = true) => {
      setSettings((prev) => {
        const updated = { ...prev, [field]: value };

        if (field === "displayFrom" || field === "displayTo") {
          const from = field === "displayFrom" ? value : prev.displayFrom;
          const to = field === "displayTo" ? value : prev.displayTo;
          const err = validateDateRange(from, to);
          setDateError(err);
          if (!err && autoSave) {
            saveSettings(updated);
          }
        } else if (autoSave) {
          setFieldErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
          });
          saveSettings(updated);
        } else {
          // Text field (no autoSave) — validate inline while typing
          const trimmed = (value || "").trim();
          if (!trimmed) {
            // Show error if field is required based on toggle state
            const errors = validateFields(updated);
            if (errors[field]) {
              setFieldErrors((prev) => ({ ...prev, [field]: errors[field] }));
            }
          } else if (
            (field === "releaseNotesUrl" || field === "feedbackLink") &&
            !isValidUrl(trimmed)
          ) {
            // Show URL format error while typing
            setFieldErrors((prev) => ({
              ...prev,
              [field]: "Please enter a valid URL (e.g., https://example.com)",
            }));
          } else {
            // Clear error when value is valid
            setFieldErrors((prev) => {
              const next = { ...prev };
              delete next[field];
              return next;
            });
          }
        }

        settingsRef.current = updated;
        return updated;
      });
    },
    [validateDateRange, saveSettings, validateFields]
  );

  const handleBlur = useCallback((field) => {
    const current = settingsRef.current;
    const errors = validateFields(current);
    if (errors[field]) {
      // For URL format errors, show the error and keep the value so user can fix it
      const isFormatError = errors[field].includes("valid URL");
      if (isFormatError) {
        setFieldErrors((prev) => ({ ...prev, [field]: errors[field] }));
        return;
      }
      // For "required" errors, revert the field to the last saved value
      const reverted = { ...current, [field]: lastSavedRef.current[field] };
      setSettings(reverted);
      settingsRef.current = reverted;
      setFieldErrors({});
      return;
    }
    setFieldErrors({});
    saveSettings(current);
  }, [saveSettings, validateFields]);

  if (loading) {
    return (
      <div
        data-testid="release-notes-page"
        className={`${classes.pageRoot} release-notes-page`}
      >
        <Typography className={classes.title} variant="h4">Release Notes and Feedback</Typography>
        <Divider className={classes.titleDivider} />  
        <Box display="flex" justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div data-testid="release-notes-page" className={`${classes.pageRoot} release-notes-page`}>
      <Typography className={classes.title} variant="h4">Release Notes and Feedback</Typography>
      <Divider className={classes.titleDivider} />
      <ReleaseNotesForm
        settings={settings}
        onChange={handleChange}
        onBlur={handleBlur}
        dateError={dateError}
        fieldErrors={fieldErrors}
      />
    </div>
  );
};

export default ReleaseNotesPage;
